import { renderRichText } from '@storyblok/react';

export type StoryblokRichText = Record<string, unknown> | string | undefined | null;

export function extractHtmlDocument(value: StoryblokRichText): string {
  if (!value) return '';
  if (typeof value === 'string') return isFullHtmlDocument(value) ? value : '';

  const doc = value as any;
  if (doc.type === 'doc' && Array.isArray(doc.content)) {
    for (const node of doc.content) {
      if (node.type === 'html' && node.attrs?.body) return node.attrs.body as string;
      if (node.type === 'code_block' && node.content?.[0]?.text) return node.content[0].text as string;
    }
  }

  return '';
}

export function resolvePreviewHtml(value: StoryblokRichText): string {
  if (!value) return '';
  if (typeof value === 'string') return value;

  const htmlDocument = extractHtmlDocument(value);
  if (htmlDocument) return htmlDocument;

  return String(renderRichText(value as unknown as Parameters<typeof renderRichText>[0]) ?? '');
}

export function stripHtmlToText(html: string, max = 200): string {
  let text = html || '';
  let previous: string;

  do {
    previous = text;
    text = text
      .replace(/<head\b[^>]*>[\s\S]*?<\/head\s*>/gi, '')
      .replace(/<title\b[^>]*>[\s\S]*?<\/title\s*>/gi, '')
      .replace(/<style\b[^>]*>[\s\S]*?<\/style\s*>/gi, '')
      .replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, '')
      .replace(/<[^>]*>?/g, ' ');
  } while (text !== previous);

  text = text.replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

export function richTextToPlainText(value: StoryblokRichText): string | null {
  if (!value) return null;

  const htmlDocument = extractHtmlDocument(value);
  if (htmlDocument) return stripHtmlToText(htmlDocument);

  if (typeof value === 'string') return value;

  const extract = (node: any): string => {
    if (!node) return '';
    if (node.type === 'text') {
      let text = node.text || '';
      const marks: string[] = (node.marks ?? []).map((m: any) => m.type);
      if (marks.includes('bold')) text = `**${text}**`;
      if (marks.includes('italic')) text = `_${text}_`;
      if (marks.includes('underline')) text = `<u>${text}</u>`;
      return text;
    }
    if (Array.isArray(node.content)) return node.content.map(extract).join(node.type === 'paragraph' ? '\n' : '');
    return '';
  };

  return extract(value) || null;
}

export function isFullHtmlDocument(html: string): boolean {
  const trimmed = html.trimStart();
  return trimmed.startsWith('<html') || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<!doctype');
}

export function withPreviewDocumentStyles(html: string): string {
  const style = '<style>body{display:block!important;align-items:initial!important;justify-content:initial!important;min-height:0!important;margin:0!important;padding:0!important;font-family:Manrope,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}img{max-width:100%;height:auto;}</style>';
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${style}</head>`);
  if (/<body[^>]*>/i.test(html)) return html.replace(/<body[^>]*>/i, (match) => `${match}${style}`);
  return `<!DOCTYPE html><html><head>${style}</head><body>${html}</body></html>`;
}
